
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from '@/integrations/supabase/client';
import { Studio } from '@/hooks/use-studios';
import { StudioCard } from './StudioCard';
import { StudioCardSkeleton } from './StudioCardSkeleton';
import { processGeoLocation, calculateDistance } from '@/utils/geo-utils';

interface RecommendedStudiosProps {
  currentStudioId: string;
  location?: {
    latitude: number;
    longitude: number;
  };
  limit?: number;
}

export const RecommendedStudios: React.FC<RecommendedStudiosProps> = ({ 
  currentStudioId, 
  location,
  limit = 3
}) => {
  const [studios, setStudios] = useState<Studio[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchNearbyStudios = async () => {
      try {
        setLoading(true);
        
        // Basic query to get studios other than the current one
        let query = supabase
          .from('studios')
          .select('id, name, description, address, is_registered, geo_location, rating_avg, rating_count, slug') as any;
          
        query = query.neq('id', currentStudioId as any);
        query = query.limit(limit);
        
        // Order by rating if we don't have location data
        if (!location || !location.latitude || !location.longitude) {
          query = query.order('rating_avg', { ascending: false });
        }
        
        const { data, error } = await query;
        
        if (error) {
          throw error;
        }
        
        if (!data || data.length === 0) {
          setStudios([]);
          return;
        }
        
        // Process studios and convert geo_location
        const processedStudios: Studio[] = data.map((studio: any) => {
          if (!studio) return null;
          
          const coordinates = processGeoLocation(studio.geo_location);
          
          return {
            id: studio.id || '',
            name: studio.name || '',
            description: studio.description || '',
            address: studio.address || '',
            slug: studio.slug || '',
            is_registered: Boolean(studio.is_registered),
            geo_location: coordinates,
            rating_avg: Number(studio.rating_avg || 0),
            rating_count: Number(studio.rating_count || 0)
          };
        }).filter(Boolean) as Studio[];
        
        // Sort by distance if location is available
        if (location && location.latitude && location.longitude) {
          processedStudios.sort((a, b) => {
            if (!a.geo_location || !b.geo_location) return 0;
            
            const distA = calculateDistance(
              location.latitude, 
              location.longitude, 
              a.geo_location.latitude, 
              a.geo_location.longitude
            );
            
            const distB = calculateDistance(
              location.latitude, 
              location.longitude, 
              b.geo_location.latitude, 
              b.geo_location.longitude
            );
            
            return distA - distB;
          });
        }
        
        setStudios(processedStudios);
      } catch (err) {
        console.error('Error fetching nearby studios:', err);
        // Fallback with empty array
        setStudios([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchNearbyStudios();
  }, [currentStudioId, location, limit]);
  
  if (loading) {
    return <StudioCardSkeleton count={limit} />;
  }
  
  if (studios.length === 0) {
    return (
      <Card className="bg-card/50 backdrop-blur-sm">
        <CardContent className="p-4 flex flex-col gap-2">
          <p className="font-medium">Keine weiteren Studios gefunden</p>
          <p className="text-sm text-zinc-400">Du kannst weitere Studios in unserer App entdecken.</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {studios.map((studio, index) => (
        <StudioCard 
          key={studio.id} 
          studio={studio} 
          index={index} 
          userLocation={location} 
        />
      ))}
    </div>
  );
};
